/**
 * THE-TAB-IS-IN-THE-MENU-1 — the tabs this account has had built, for the menu.
 *
 * The same three states as `useBuiltThings`, and the menu only ever draws the
 * third: still asking and a failed read both contribute nothing, because a tab
 * invented from a failed read is a promise of a screen that may not exist.
 *
 * WITHOUT A RELOAD. It re-reads on `refreshAccountNav()`, which the panel
 * already calls when a build lands, so a tab and a hidden section arrive on the
 * same signal.
 */
import { useEffect, useState } from 'react'
import { useOptionalAuth } from '../providers/AuthProvider'
import { listBuiltTabRows } from '../lib/systemMapRepo'
import { tabsFromMap, type BuiltTab } from '../lib/builtTabs'
import { onAccountNavRefresh } from './useAccountNavConfig'
import { sharedRead } from '../lib/sharedRead'

export type BuiltTabsState =
  | { state: 'loading' }
  | { state: 'failed'; error: string }
  | { state: 'ready'; tabs: BuiltTab[] }

export function useBuiltTabs(): BuiltTabsState {
  const subAccountId = useOptionalAuth()?.currentSubAccountId ?? null
  const [state, setState] = useState<BuiltTabsState>({ state: 'loading' })
  const [nonce, setNonce] = useState(0)

  useEffect(() => onAccountNavRefresh(() => setNonce((n) => n + 1)), [])

  useEffect(() => {
    let cancelled = false
    if (!subAccountId) {
      setState({ state: 'ready', tabs: [] })
      return
    }
    // THE-HOME-LOADS-FAST-1 — one read, joined. `nonce` is in the key so a
    // refresh never joins the read it is asking to be redone.
    void sharedRead(`built-tabs:${subAccountId}:${nonce}`, () => listBuiltTabRows(subAccountId))
      .then((rows) => {
        if (!cancelled) setState({ state: 'ready', tabs: tabsFromMap(rows) })
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setState({ state: 'failed', error: e instanceof Error ? e.message : 'read failed' })
        }
      })
    return () => {
      cancelled = true
    }
  }, [subAccountId, nonce])

  return state
}

/** The tabs a state may contribute. Only a completed read contributes any. */
export function tabsForMenu(state: BuiltTabsState): BuiltTab[] {
  return state.state === 'ready' ? state.tabs : []
}
